import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Coffee, Save } from "lucide-react";

export default function BreakPolicyPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tenant/config/breaks");
        setCfg(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/tenant/config/breaks", cfg);
      setCfg(data);
      toast.success("Break policy saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading || !cfg) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const example = Math.max(0, (cfg.auto_deduct_break_enabled ? cfg.auto_deduct_break_minutes : 0));
  const netHours = ((480 - (cfg.auto_deduct_break_enabled && 480 > cfg.auto_deduct_break_after_minutes ? cfg.auto_deduct_break_minutes : 0)) / 60).toFixed(2);

  return (
    <div className="space-y-6" data-testid="break-policy-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Time admin</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Break policy</h1>
        <p className="mt-2 text-muted-foreground">Auto-deduct unpaid meal breaks from long shifts. Workers can still log explicit breaks — auto-deduct tops up to your minimum.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Coffee className="h-5 w-5" /> Auto-deduct meal break</CardTitle>
          <CardDescription>Applies only when a shift exceeds the threshold and worker hasn't logged an explicit unpaid break of the same length.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-md border bg-muted/40 p-3">
            <div>
              <div className="font-medium">Auto-deduct enabled</div>
              <div className="text-xs text-muted-foreground">Turn off if your workers always log meal breaks manually.</div>
            </div>
            <Switch checked={cfg.auto_deduct_break_enabled} onCheckedChange={(v) => setCfg({ ...cfg, auto_deduct_break_enabled: v })} data-testid="break-auto-toggle" />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Deduct amount (minutes)</Label>
              <Input type="number" min={0} max={120} value={cfg.auto_deduct_break_minutes} onChange={(e) => setCfg({ ...cfg, auto_deduct_break_minutes: Number(e.target.value || 0) })} data-testid="break-minutes" />
            </div>
            <div className="space-y-2">
              <Label>Applies after (minutes worked)</Label>
              <Input type="number" min={0} max={720} value={cfg.auto_deduct_break_after_minutes} onChange={(e) => setCfg({ ...cfg, auto_deduct_break_after_minutes: Number(e.target.value || 0) })} data-testid="break-after" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Example</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>An <strong>8 hour</strong> shift (480 min):</div>
          <div className="rounded-lg border bg-accent/40 p-3">
            <div>Auto-deducted: <span className="font-mono">{cfg.auto_deduct_break_enabled && 480 > cfg.auto_deduct_break_after_minutes ? example : 0} min</span></div>
            <div>Net paid: <span className="font-mono font-semibold text-primary">{netHours}h</span></div>
          </div>
          <p className="text-muted-foreground">If the worker logs an explicit unpaid break equal to or greater than the deduct amount, the auto-deduct does not apply.</p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="break-save-btn"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save policy"}</Button>
      </div>
    </div>
  );
}
